<template>
	<div :style="getStyles()">
		<slot/>
	</div>
</template>

<script>
// Imports
require('ol/ol.css');

// Import OL Containers
import Map from 'ol/Map';
import View from 'ol/View'

import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'

// import BaseView from './BaseView.vue';
import { RENDERER_TYPE } from '../utils/consts'
import compiler from '../mixins/compilers/_compiler.js'
import layersContainer from '../mixins/containers/layers-container.js'
import sourceContainer from '../mixins/containers/source-container.js'
import sources from '../mixins/containers/sources.js'
import mergeDescriptors from '../utils/merge-descriptors.js'
import { IndexedCollectionAdapter, SourceCollectionAdapter } from '../utils/collection'
import {merge as mergeObs} from 'rxjs'
import { distinctUntilChanged, map as mapObs, throttleTime } from 'rxjs/operators'
import observableFromOlEvent from '../mixins/events/from-ol-event.js'
import {isEqual} from '../utils/util.js'

  const props = /** @lends module:map/map# */{
    /**
     * Options for default controls added to the map by default. Set to `false` to disable all map controls. Object
     * value is used to configure controls.
     * @type {Object|boolean}
     * @todo remove when vl-control-* components will be ready
     */
    controls: {
      type: [Object, Boolean],
      default: true,
    },
    /**
     * The element to listen to keyboard events on. For example, if this option is set to `document` the keyboard
     * interactions will always trigger. If this option is not specified, the element the library listens to keyboard
     * events on is the component root element.
     * @type {string|Element|Document}
     */
    keyboardEventTarget: [String, Element, Document],
    /**
     * When set to `true`, tiles will be loaded during animations.
     * @type {boolean}
     */
    loadTilesWhileAnimating: {
      type: Boolean,
      default: false,
    },
    /**
     * When set to `true`, tiles will be loaded while interacting with the map.
     * @type {boolean}
     */
    loadTilesWhileInteracting: {
      type: Boolean,
      default: false,
    },
    /**
     * The map logo. If a **string** is provided, it will be set as the image source of the logo. If an **object** is provided,
     * the `src` property should be the **URL** for an image and the `href` property should be a **URL** for creating a link.
     * If an **element** is provided, the **element** will be used. To disable the map logo, set the option to `false`.
     * By default, the **OpenLayers** logo is shown.
     * @type {boolean|string|Object|Element}
     */
    logo: [String, Object, Element, Boolean],
    /**
     * The minimum distance in pixels the cursor must move to be detected as a map move event instead of a click.
     * Increasing this value can make it easier to click on the map.
     * @type {Number}
     */
    moveTolerance: {
      type: Number,
      default: 1,
    },
    /**
     * The ratio between physical pixels and device-independent pixels (dips) on the device.
     * @type {number}
     */
    pixelRatio: {
      type: Number,
      default: () => window.devicePixelRatio || 1,
    },
    /**
     * Renderer. By default, **Canvas** and **WebGL** renderers are tested for support in that order,
     * and the first supported used. **Note** that the **Canvas** renderer fully supports vector data,
     * but **WebGL** can only render **Point** geometries.
     * @type {string|string[]}
     * @default ['canvas', 'webgl']
     */
    renderer: {
      type: [String, Array],
      default: () => [RENDERER_TYPE.CANVAS, RENDERER_TYPE.WEBGL],
    },
    /**
     * Root element `tabindex` attribute value. Value should be provided to allow keyboard events on map.
     * @type {number|string}
     */
    tabindex: [String, Number],
    /**
     * Projection for input/output coordinates in plain data.
     * @type {string}
     */
    dataProjection: String,

    mapHeight: {
      type: String,
      default: '100vh'
    },

    mapWidth: {
      type: String,
      default: '100%'
    }
  }

const watch = {
  dataProjection (value) {
    if (this.$map) {
      this.$map.set('dataProjection', value)
      this.refresh()
    }
  },
  mapHeight (value) {
    // console.log("Changed")
    // console.log(value)
  }
}

const methods = {
	createOlObject () {
		// console.log("createOlObject Map.vue")
      const map = new Map({
        // controls: [],
        // loadTilesWhileAnimating: this.loadTilesWhileAnimating,
        // loadTilesWhileInteracting: this.loadTilesWhileInteracting,
        // pixelRatio: this.pixelRatio,
        // renderer: this.renderer,
        // logo: this.logo,
        // keyboardEventTarget: this.keyboardEventTarget,
        view: this._view,
      })
      // ol.Map constructor can create default view if no provided with options
      this._view = map.getView()
      // add default overlay to map
      this._defaultLayer.setMap(map)
      // if (this.controls) {
      //   let opts = typeof this.controls === 'object' ? this.controls : undefined
      //   map.getControls().extend(olcontrol.defaults(opts).getArray())
      // }
      map.set('dataProjection', this.dataProjection)
      return map
    },
    getLayersTarget () {
      // hasMap(this)
      if (this._layersTarget == null) {
        this._layersTarget = new IndexedCollectionAdapter(
          this.$map.getLayers(),
          layer => layer.get('id'),
        )
      }
      return this._layersTarget
    },
    getServices () {
      const vm = this
      return mergeDescriptors(
        compiler.methods.getServices.call(this),
        layersContainer.methods.getServices.call(this),
        sources.methods.getServices.call(this),
        {
          get map () { return vm.$map },
          get view () { return vm.$view },
          get viewContainer () { return vm.$viewContainer },
          get mapContainer () { return vm },
        }
      )
    },
    setView (view) {
      view = view instanceof Vue ? view.$view : view
      view || (view = new View())
      if (view !== this._view) {
        this._view = view
      }
      if (this.$map && view !== this.$map.getView()) {
        this.$map.setView(view)
      }
    },
    setViewContainer (viewContainer) {
      this._viewContainer = viewContainer
    },
    mount () {
    	// console.log("Mount BaseMap.vue")
  		this.$map.setTarget(this.$el)
  		this.subscribeAll()
      this.updateSize()
	},
	unmount () {
      // hasMap(this)
      this.unsubscribeAll()
      this.$map.setTarget(undefined)
    },
    /**
     * Updates map size and re-renders map.
     * @return {Promise}
     */
    refresh () {

      this.updateSize()
      return this.render()
        .then(() => compiler.methods.refresh.call(this))
    },
    /**
     * @return {Promise}
     */
    render () {
      return new Promise(resolve => {
      	// console.log("Render BaseMap.vue")
        // hasMap(this)
        this.$map.once('postrender', () => resolve())
        this.$map.render()

      })
    },
    /**
     * @return {void}
     * @protected
     */
    subscribeAll () {
        subscribeToMapEvents.call(this);
    },
    /**
     * Updates map size.
     * @return {void}
     */
    updateSize () {

      // hasMap(this)
      this.$map.updateSize()
    },
    getStyles () {
      let styles = ""
      styles+='height:'+this.mapHeight+";"
      styles+='width:'+this.mapWidth+";"
      return styles;
    }
}

export default {

  name: 'BaseMap',
  props,
  methods,
  watch,
  mixins: [compiler, layersContainer, sourceContainer, sources],
  data () {
    return {
    	// view: new View({
    	// 	center: [0, 0]
    	// })
    }
  },
  created () {
  	this._view = undefined
    this._viewContainer = undefined
    this.$on("view-container-created",(e)=>this.setViewContainer(e))

    // this._layers = []

  	this._defaultLayer = new VectorLayer({
  		source: new VectorSource()
  	})

  	Object.defineProperties(this, /** @lends module:map/map# */{
        /**
         * OpenLayers map instance.
         * @type {ol.Map|undefined}
         */
        $map: {
          enumerable: true,
          get: () => this.$olObject,
        },
        /**
         * OpenLayers view instance.
         * @type {ol.View|undefined}
         */
        $view: {
          enumerable: true,
          get: () => this._view,
        },
        $viewContainer: {
          enumerable: true,
          get: () => this._viewContainer,
        },

        // $layers: {
        //   enumerable: true,
        //   get: () => this._layers,
        // },
      })
  }
}


  /**
   * Subscribe to OL map events.
   *
   * @return {void}
   * @private
   */
  function subscribeToMapEvents () {
    // hasMap(this)
    // hasView(this)
    const ft = 100
    // pointer
    const pointerEvents = mergeObs(
        observableFromOlEvent(this.$map, [
            'click',
            'dblclick',
            'singleclick'
        ]),
        observableFromOlEvent(this.$map, [
            'pointerdrag',
            'pointermove',
        ]).pipe(
            throttleTime(ft),
            distinctUntilChanged((a, b) => {
                isEqual(a.coordinate, b.coordinate)
            }),
        ),
    ).pipe(
        mapObs(evt => ({
            ...evt
        }))
    )
    const otherEvents = observableFromOlEvent(this.$map, [
      'movestart',
      'moveend',
      'postrender',
      'precompose',
      'postcompose',
    ])
    const events = mergeObs(pointerEvents, otherEvents)
    this.subscribeTo(events, evt => this.$emit(evt.type, evt))
  }


</script>

<style lang="css" scoped>
.map-container {
	height: 100%;
}
</style>



// WEBPACK FOOTER //
// resources/js/map/MapComponents/Base/BaseMap.vue